import { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import { formatDateTime } from "@/lib/utils";
import { Link } from "react-router-dom";

export default function NotificationsPage() {
  const [items, setItems] = useState(null);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = () =>
  api.get("/notifications").then(({ data }) => {
    setItems(data);
    setLastUpdated(new Date());
  });
  useEffect(() => { load(); api.post("/notifications/mark-read"); }, []);

  if (!items) return <LoadingScreen />;

  const archive = Object.values(
    items.reduce((acc, item) => {
      const date = new Date(item.created_at);

      const key = `${date.getFullYear()}-${date.getMonth()}`;

      if (!acc[key]) {
        acc[key] = {
          key,
          label: date.toLocaleString("default", {
            month: "long",
            year: "numeric",
          }),
          count: 0,
        };
      }

      acc[key].count++;

      return acc;
    }, {})
  ).sort((a, b) => b.key.localeCompare(a.key));


  const filteredItems = selectedArchive
    ? items.filter((item) => {
      const date = new Date(item.created_at);
      return `${date.getFullYear()}-${date.getMonth()}` === selectedArchive;
    })
    : items;


  const getNotificationStyle = (title = "") => {
    const text = title.toLowerCase();

    if (text.includes("submitted")) {
      return {
        bg: "bg-blue-100",
        icon: "text-blue-600",
      };
    }

    if (text.includes("running")) {
      return {
        bg: "bg-amber-100",
        icon: "text-amber-600",
      };
    }

    if (text.includes("review")) {
      return {
        bg: "bg-purple-100",
        icon: "text-purple-600",
      };
    }

    if (text.includes("completed") || text.includes("success")) {
      return {
        bg: "bg-green-100",
        icon: "text-green-600",
      };
    }

    if (text.includes("failed") || text.includes("error")) {
      return {
        bg: "bg-red-100",
        icon: "text-red-600",
      };
    }

    return {
      bg: "bg-gray-100",
      icon: "text-gray-600",
    };
  };

  const stats = {
    total: filteredItems.length,
    unread: filteredItems.filter((n) => !n.read).length,
    submitted: filteredItems.filter((n) =>
      n.title.toLowerCase().includes("submitted")
    ).length,
    running: filteredItems.filter((n) =>
      n.title.toLowerCase().includes("running")
    ).length,
    completed: filteredItems.filter((n) =>
      n.title.toLowerCase().includes("completed")
    ).length,
  };


  return (
    <div className="grid grid-cols-1 xl:grid-cols-[65%_35%] gap-6 items-start">
      <div>
        {selectedArchive && (
          <button
            type="button"
            onClick={() => setSelectedArchive(null)}
            className="mt-4 mb-4 text-sm font-medium text-[#0B6936] hover:underline"
          >
            ← Back to Recent Notifications
          </button>
        )}
        {filteredItems.length === 0 ? (
          <div className="sqit-card p-12 text-center">
            <Bell className="w-8 h-8 mx-auto text-gray-300" />
            <p className="mt-4 text-gray-500">No notifications yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredItems.map((n) => {
              const style = getNotificationStyle(n.title);

              return (
                <Link
                  key={n.id}
                  to={n.link || "/app/dashboard"}
                  className={`block sqit-card p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ${!n.read ? "border-[#0B6936]/30 bg-[#0B6936]/5" : ""
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${style.bg}`}
                    >
                      <Bell size={14} className={style.icon} />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {!n.read && (
                            <span className="w-2 h-2 rounded-full bg-[#0B6936] flex-shrink-0" />
                          )}

                          <div className="font-semibold text-gray-900 text-sm truncate">
                            {n.title}
                          </div>
                        </div>

                        <div className="text-xs text-gray-400 whitespace-nowrap">
                          {formatDateTime(n.created_at)}
                        </div>
                      </div>

                      <p className="mt-1 text-sm text-gray-700">
                        {n.body}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
      <div className="sqit-card p-6 h-fit xl:sticky xl:top-24">
        <h2 className="font-display text-xl font-semibold text-gray-900">
          Notification Center
        </h2>

        <p className="mt-1 text-sm text-gray-500">
          Overview of your recent activity.
        </p>

        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            {selectedArchive
              ? archive.find((a) => a.key === selectedArchive)?.label
              : "Recent Notifications"}
          </h3>

          <div className="mt-4 space-y-3">

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Total
              </span>
              <span className="font-semibold">
                {stats.total}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Submitted
              </span>
              <span className="font-semibold">
                {stats.submitted}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Running
              </span>
              <span className="font-semibold">
                {stats.running}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Completed
              </span>
              <span className="font-semibold">
                {stats.completed}
              </span>
            </div>

          </div>
        </div>
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Archive
          </h3>
          <div className="mt-4 max-h-64 overflow-y-auto space-y-2 pr-1">
            {archive.map((month) => (
              <button
                key={month.key}
                type="button"
                onClick={() => setSelectedArchive(month.key)}
                className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm transition ${selectedArchive === month.key
                  ? "bg-[#0B6936] text-white shadow-sm ring-1 ring-[#0B6936]"
                  : "hover:bg-gray-50"
                  }`}
              >
                <span className={selectedArchive === month.key ? "text-white" : "text-gray-700"}>
                  {month.label}
                </span>

                <span className={selectedArchive === month.key ? "text-white/80" : "text-gray-500"}>
                  ({month.count})
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Quick Actions
          </h3>

          <div className="mt-4 space-y-3">

            <button
              type="button"
              onClick={load}
              className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Refresh Notifications
            </button>

            {lastUpdated && (
              <p className="text-xs text-gray-500 text-center">
                Last updated: {formatDateTime(lastUpdated)}
              </p>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
